import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {ConfirmDialog} from '@/components/confirm-dialog'
import {gateTooltip} from './gate-tooltip'
import {useFinishAutoevaluacion, type SgsstAssessment} from '@/hooks/sgsst/evaluacion/use-sgsst'
import {formatRatePct} from '@/lib/risk'
import {translateApiError} from '@/lib/errors'
import {track} from '@/lib/telemetry'
import {cn} from '@/lib/utils'

export function TerminarControls({
  businessId,
  assessment,
  canAttest,
  firmaBlockedReason,
  onFinished,
}: {
  businessId: string
  assessment: SgsstAssessment
  canAttest: boolean
  firmaBlockedReason: string | null
  onFinished: () => void
}) {
  const {t} = useTranslation()
  const finish = useFinishAutoevaluacion(businessId)
  const [confirming, setConfirming] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const unmarked = assessment.items.filter((item) => !item.status).length
  const total = assessment.items.length
  // Gated by `canAttest`, not `editable`: «terminar» is a legal attestation (marcado §4.1).
  const trigger = (
    <Button
      size="sm"
      disabled={unmarked > 0 || !canAttest}
      title={
        unmarked > 0
          ? t('sgsst.terminar.blockedTooltip', {count: unmarked})
          : gateTooltip(canAttest, firmaBlockedReason, t)
      }
      onClick={() => setConfirming(true)}
      data-testid="terminar-button">
      {t('sgsst.terminar.action')}
    </Button>
  )
  const hint =
    error ??
    (unmarked > 0 ? t('sgsst.terminar.remaining', {count: unmarked}) : t('sgsst.terminar.allMarked', {count: total}))

  return (
    <div className="flex flex-col items-end gap-1">
      {trigger}
      <span className={cn('text-xs', error ? 'text-destructive' : 'text-muted-foreground')}>{hint}</span>
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title={t('sgsst.terminar.confirmTitle', {year: assessment.period_year})}
        description={t('sgsst.terminar.confirmBody', {
          year: assessment.period_year,
          pct: formatRatePct(assessment.score.total_pct),
          band: t(`sgsst.bands.${assessment.score.band}.label`),
        })}
        confirmLabel={t('sgsst.terminar.confirmAction')}
        cancelLabel={t('common.cancel')}
        pending={finish.isPending}
        onConfirm={() =>
          finish.mutate(undefined, {
            onSuccess: () => {
              track('autoevaluacion_completada', {
                banda: assessment.score.band,
                score_pct: assessment.score.total_pct,
                chapter: assessment.chapter,
              })
              setConfirming(false)
              onFinished()
            },
            onError: (err) => {
              setConfirming(false)
              setError(translateApiError(err))
            },
          })
        }
        porque={{
          label: t('sgsst.terminar.porqueLabel'),
          testId: 'terminar-porque',
          contenidoTestId: 'terminar-porque-contenido',
          contenido: (
            <ul className="list-disc space-y-1 pl-4">
              <li>{t('sgsst.terminar.porqueRegistro')}</li>
              <li>{t('sgsst.terminar.porqueMarcado')}</li>
              <li>{t('sgsst.terminar.porqueCorreccion')}</li>
              <li>{t('sgsst.terminar.porqueCierre')}</li>
            </ul>
          ),
        }}
      />
    </div>
  )
}
